/**
 * MaterialPreviews - Editor utility for material slot detection and preview generation
 *
 * This is an editor/tooling concern, separate from the core Renderer behavior.
 * Use this to detect material slots and generate preview thumbnails for UI.
 */

import { Material } from "@babylonjs/core/Materials/material";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { Scene } from "@babylonjs/core/scene";
import { SceneObject } from "../core/SceneObject";
import { Renderer } from "../behaviors/renderer";
import { MaterialData } from "../scenedata/behaviors/rendererdata";
import { PreviewRenderer } from "./PreviewRenderer";
import { unmarkOccluderMaterial } from "../loaders/gltf/TRACE_materials_occlusion";
import type { ContainerLoadOptions, ResourceManager } from "../loaders/ResourceManager";
import { Logger } from "../utils/Logger";
import { cloneMaterial } from "../utils/materialClone";

/**
 * A material asset is a glTF whose payload is the MATERIAL, with no mesh
 * primitive referencing it — so Babylon instantiates nothing unless the load
 * asks for every material. See {@link ContainerLoadOptions.allMaterials}.
 */
const MATERIAL_ASSET_CONTAINER_OPTS: ContainerLoadOptions = { allMaterials: true };

/**
 * Material slot interface for detection results.
 * Represents a detected material slot on a 3D object.
 * This is an editor-only type used for building material editing UI.
 */
export interface MaterialSlot {
    /** Name of the material slot */
    slotName: string;
    /** Current material name */
    currentMaterial: string;
    /** Number of meshes using this material */
    meshCount: number;
    /** Names of meshes using this material */
    meshes: string[];
    /** Whether a replacement material has been applied */
    replacementApplied?: boolean;
    /**
     * Authored as flat content (KHR_materials_unlit) — a text label, image
     * plane, or baked UI panel whose look IS its texture. Adoption treats
     * these as content to preserve, not surfaces to theme.
     */
    unlit?: boolean;
    /** URL to replacement GLB file (if applied) */
    replacementGLB?: string;
    /** Index of this material slot (0-based) */
    materialIndex?: number;
    /** Albedo/base color in hex format */
    albedoColor?: string;
    /** UV scale factor */
    uvScale?: number;
    /** Base64 preview image of the material */
    previewImage?: string;
}

/** What a material ASSET yields: the rendered ball plus the material's own name. */
export interface MaterialAssetPreview {
    /** PNG data URL of the rendered preview. */
    image: string;
    /** The material's authored name, as it appears in the glTF. */
    materialName: string;
}

/**
 * Static utility class for material slot detection and preview generation.
 * Owns the shared PreviewRenderer instance.
 */
export class MaterialPreviews {
    /** Shared PreviewRenderer instance */
    private static previewRenderer: PreviewRenderer = new PreviewRenderer();

    /**
     * Detect material slots and generate preview images.
     *
     * Materials must already be ready to render (shaders compiled, textures
     * loaded). Call after the scene has finished loading.
     *
     * @param sceneObject - The scene object to detect materials on
     * @param previewSize - Optional preview render resolution in pixels (default 128)
     * @returns Array of material slots with preview images
     */
    public static async detectMaterialSlotsWithPreviews(
        sceneObject: SceneObject,
        previewSize?: number,
    ): Promise<MaterialSlot[]> {
        const slots = MaterialPreviews.buildMaterialSlots(sceneObject);
        // No mesh slots: the object may BE a material rather than wear one.
        if (slots.length === 0) return MaterialPreviews.materialAssetSlots(sceneObject, previewSize);

        const scene = sceneObject.transform.getScene();

        // Generate preview images
        await Promise.all(slots.map(async (slot) => {
            try {
                const material = MaterialPreviews.getMaterialFromSlot(sceneObject, slot);
                if (material) {
                    slot.previewImage = await MaterialPreviews.previewRenderer.renderMaterialPreview(
                        material,
                        scene,
                        sceneObject.name,
                        slot.slotName,
                        false, // Use cache
                        previewSize,
                    );
                }
            } catch (error) {
                Logger.warn(`[MaterialPreviews] Failed to generate preview for slot ${slot.slotName}:`, error);
            }
        }));

        return slots;
    }

    /**
     * Detect material slots WITHOUT rendering previews — the light variant for
     * data-level consumers (e.g. the add-state material snapshot) that need
     * slot indices, current sources, and albedo colors but no imagery.
     */
    public static detectMaterialSlots(sceneObject: SceneObject): MaterialSlot[] {
        return MaterialPreviews.buildMaterialSlots(sceneObject);
    }

    /**
     * Build material slots from a scene object.
     * Works with or without a Renderer behavior.
     */
    private static buildMaterialSlots(sceneObject: SceneObject): MaterialSlot[] {
        // Use the Renderer's pinned slot map when available for stable indices;
        // fall back to live scene graph query for objects without a Renderer.
        const renderer = sceneObject.getBehavior(Renderer);
        const meshSlots = renderer?.getMeshSlots();
        const meshEntries: [number, AbstractMesh][] = meshSlots && meshSlots.size > 0
            ? Array.from(meshSlots)
            : MaterialPreviews.getMeshes(sceneObject).map((m, i) => [i, m]);

        Logger.log(`[MaterialPreviews] buildMaterialSlots for ${sceneObject.name}: found ${meshEntries.length} meshes, hasRenderer: ${!!renderer}`);

        // Debug: log mesh names and materials
        meshEntries.forEach(([idx, mesh]) => {
            Logger.log(`[MaterialPreviews]   Mesh ${idx}: "${mesh.name}", hasMaterial: ${!!mesh.material}, materialName: ${mesh.material?.name || 'N/A'}`);
        });

        // Build original material lookups directly from Renderer data
        const originalMaterialNames = renderer?.getOriginalMaterials() ?? {};
        const scene = sceneObject.transform.getScene();

        const originalMaterials = new Map<string, Material>();
        const meshIndexToOriginalMaterial = new Map<number, string>();
        for (const [indexStr, materialName] of Object.entries(originalMaterialNames)) {
            meshIndexToOriginalMaterial.set(parseInt(indexStr, 10), materialName);
            if (!originalMaterials.has(materialName)) {
                const mat = scene.getMaterialByName(materialName);
                if (mat) originalMaterials.set(materialName, mat);
            }
        }
        const rendererData = renderer?.getData();
        const materials = rendererData?.materials || [];

        // Build slots from original materials (before replacement)
        const baseSlots: MaterialSlot[] = [];

        meshEntries.forEach(([meshIndex, mesh]) => {
            // Check both live material and original material record — the slot exists
            // even when mesh.material is temporarily null during async load.
            const originalMaterialName = meshIndexToOriginalMaterial.get(meshIndex) || mesh.material?.name;

            if (mesh.material || originalMaterialName) {
                const resolvedOriginalName = originalMaterialName || mesh.material!.name;
                const originalMaterial = originalMaterials.get(resolvedOriginalName) || mesh.material;
                const materialName = originalMaterial?.name || resolvedOriginalName || "UnnamedMaterial";

                // Extract color from original material
                let currentColor: string | undefined;
                if (originalMaterial instanceof PBRMaterial && originalMaterial.albedoColor) {
                    currentColor = originalMaterial.albedoColor.toHexString();
                } else if (originalMaterial instanceof StandardMaterial && originalMaterial.diffuseColor) {
                    currentColor = originalMaterial.diffuseColor.toHexString();
                }

                const slot: MaterialSlot = {
                    slotName: materialName,
                    currentMaterial: materialName,
                    meshCount: 1,
                    meshes: [mesh.name],
                    replacementApplied: false,
                    albedoColor: currentColor,
                    materialIndex: meshIndex,
                    ...(originalMaterial instanceof PBRMaterial && originalMaterial.unlit ? { unlit: true } : {}),
                };

                baseSlots.push(slot);
            }
        });

        // Enhance slots with Renderer data
        const rendererMap = new Map<number, MaterialData>();
        materials.forEach((mat) => {
            rendererMap.set(mat.targetMaterialIndex ?? 0, mat);
        });

        const enhancedSlots = baseSlots.map((slot, index) => {
            // CRITICAL: Use slot.materialIndex (mesh index) to look up materials, not array index
            // This ensures we match materials correctly even when slots are filtered or reordered
            const key = slot.materialIndex ?? index;
            const rendererMat = rendererMap.get(key);
            if (rendererMat) {
                // A slot wearing a replacement GLB shows THAT material's colour,
                // but the base pass read it off the pre-replacement material in
                // `getOriginalMaterials()` — a colour the replacement never had.
                // Returns null when no replacement is loaded (including an
                // unresolved `$material` binding), leaving the base pass's value.
                const replacementColor = renderer?.getReplacementAlbedoHex(key);
                return {
                    ...slot,
                    replacementApplied: !!rendererMat.src,
                    replacementGLB: rendererMat.src || undefined,
                    albedoColor: rendererMat.baseColorFactor || replacementColor || slot.albedoColor,
                    uvScale: rendererMat.texture_transform?.scale?.[0] || slot.uvScale
                };
            }
            return slot;
        });

        Logger.log(`[MaterialPreviews] Detected ${enhancedSlots.length} material slots for ${sceneObject.name}`);
        return enhancedSlots;
    }

    /**
     * Render a preview for a MATERIAL ASSET — a glTF/GLB whose payload is a
     * material, carrying no mesh primitive to hang a slot off.
     *
     * The mesh-slot path ({@link detectMaterialSlotsWithPreviews}) cannot reach
     * one of these: it enumerates meshes, and a material asset has none, so it
     * reports zero slots and callers fall back to capturing an empty scene —
     * which is exactly how an uploaded material got a blank thumbnail
     * (trace-viewer#788). This is the path that has no mesh in it at all: load
     * the container asking for every material, put one on the shared preview
     * sphere, render.
     *
     * The container ref is released on every path. The preview renders from a
     * throwaway CLONE, never the container's own material: the source is shared
     * with every object that applies this asset, and the preview has to adjust
     * flags on what it renders.
     *
     * Always re-renders rather than serving a cached image — the same `src` can
     * hold new bytes after a re-upload, and nothing here can tell. The stable
     * cache key keeps that from accumulating a dead full-size data URL per call.
     *
     * @param src - Asset path or URL, as {@link ResourceManager} addresses it.
     * @returns The preview, or `undefined` when the asset holds no material at
     *   that index or could not be loaded.
     */
    public static async renderMaterialAssetPreview(
        src: string,
        scene: Scene,
        resources: ResourceManager,
        options?: { size?: number; materialIndex?: number },
    ): Promise<MaterialAssetPreview | undefined> {
        if (!src) return undefined;
        const materialIndex = options?.materialIndex ?? 0;

        let container;
        try {
            container = await resources.getOrLoadContainer(src, scene, undefined, MATERIAL_ASSET_CONTAINER_OPTS);
        } catch (error) {
            Logger.warn(`[MaterialPreviews] Could not load material asset ${src}:`, error);
            return undefined;
        }

        try {
            const source = container.materials[materialIndex];
            if (!source) {
                Logger.warn(`[MaterialPreviews] No material at index ${materialIndex} in ${src}`);
                return undefined;
            }

            const materialName = source.name || "UnnamedMaterial";
            const preview = cloneMaterial(source, `__material_asset_preview__${materialName}`);
            if (!preview) {
                Logger.warn(`[MaterialPreviews] Could not clone material ${materialName} from ${src}`);
                return undefined;
            }

            try {
                // An occluder writes depth and no colour while play mode or XR is
                // active, and OccluderMode drives that off the marker `clone`
                // copied. A marked clone would render as nothing — the blank this
                // whole path exists to fix. Its PBR block IS the intended preview
                // (see TRACE_materials_occlusion), so drop the marker and pin
                // colour write on; unmarking rebuilds the metadata rather than
                // editing the object the source shares with it.
                unmarkOccluderMaterial(preview);
                preview.disableColorWrite = false;

                const image = await MaterialPreviews.previewRenderer.renderMaterialPreview(
                    preview,
                    scene,
                    undefined,
                    undefined,
                    true, // always fresh — `src` is not a content identity
                    options?.size,
                    JSON.stringify({ materialAsset: src, materialIndex, size: options?.size }),
                );

                return image ? { image, materialName } : undefined;
            } finally {
                // The render task is done; disposing the preview also releases its texture copies.
                preview.dispose();
            }
        } finally {
            resources.releaseContainer(src, MATERIAL_ASSET_CONTAINER_OPTS);
        }
    }

    /**
     * The material-asset reading of an object with no mesh slots, as a single
     * synthetic slot so callers keep one shape to consume.
     *
     * Deliberately NOT gated on file extension: a library material reference is
     * `<api>/assets/{guid}/file` and carries none (CONTENT_MODEL_SPEC §3.4).
     * Anything that is not a material simply yields no material and an empty
     * list, at the cost of one ref-counted container load.
     */
    private static async materialAssetSlots(
        sceneObject: SceneObject,
        previewSize?: number,
    ): Promise<MaterialSlot[]> {
        const src = sceneObject.src;
        if (!src) return [];

        const preview = await MaterialPreviews.renderMaterialAssetPreview(
            src,
            sceneObject.transform.getScene(),
            sceneObject.resources,
            { size: previewSize },
        );
        if (!preview) return [];

        Logger.log(`[MaterialPreviews] ${sceneObject.name} is a material asset (${preview.materialName})`);
        return [{
            slotName: preview.materialName,
            currentMaterial: preview.materialName,
            meshCount: 0,
            meshes: [],
            materialIndex: 0,
            previewImage: preview.image,
        }];
    }

    /**
     * Get the meshes a scene object OWNS (child SceneObjects' meshes excluded).
     * Delegates to getOwnedMeshes() for consistent ordering with Renderer.
     */
    private static getMeshes(sceneObject: SceneObject): AbstractMesh[] {
        return sceneObject.getOwnedMeshes();
    }

    /**
     * Get material from a slot by its materialIndex.
     */
    private static getMaterialFromSlot(sceneObject: SceneObject, slot: MaterialSlot): Material | null {
        if (slot.materialIndex === undefined) return null;

        // Use the Renderer's pinned slot map when available for stable lookup
        const renderer = sceneObject.getBehavior(Renderer);
        const meshSlots = renderer?.getMeshSlots();
        if (meshSlots && meshSlots.size > 0) {
            const mesh = meshSlots.get(slot.materialIndex);
            return mesh?.material ?? null;
        }

        // Fallback to live scene graph query
        const meshes = MaterialPreviews.getMeshes(sceneObject);
        if (slot.materialIndex < meshes.length) {
            const mesh = meshes[slot.materialIndex];
            if (mesh?.material) {
                return mesh.material;
            }
        }

        return null;
    }

    /**
     * Regenerate the preview image for a single slot.
     * Invalidates the cache for that slot, re-reads the live material, and renders a new preview.
     *
     * @param previewSize - Optional preview render resolution in pixels (default 128)
     * @returns The updated preview data URL, or null if the slot was not found.
     */
    public static async regenerateSlotPreview(
        sceneObject: SceneObject,
        materialIndex: number,
        slotName: string,
        previewSize?: number,
    ): Promise<string | null> {
        const objectName = sceneObject.name;
        MaterialPreviews.previewRenderer.invalidateSlotCache(objectName, slotName);

        // Build a minimal slot to look up the material
        const lookupSlot: MaterialSlot = {
            slotName,
            currentMaterial: slotName,
            meshCount: 0,
            meshes: [],
            materialIndex,
        };

        const material = MaterialPreviews.getMaterialFromSlot(sceneObject, lookupSlot);
        if (!material) return null;

        const scene = sceneObject.transform.getScene();
        return MaterialPreviews.previewRenderer.renderMaterialPreview(
            material,
            scene,
            objectName,
            slotName,
            true, // force regenerate
            previewSize,
        );
    }

    /**
     * Invalidate preview cache for a specific material slot.
     * Call after material changes via commit API.
     *
     * @param objectName - Name of the scene object
     * @param slotName - Name of the material slot
     */
    public static invalidateSlotPreview(objectName: string, slotName: string): void {
        MaterialPreviews.previewRenderer.invalidateSlotCache(objectName, slotName);
    }

    /**
     * Invalidate preview cache for all material slots on an object.
     * Call after materials are reset or significantly changed.
     *
     * @param objectName - Name of the scene object
     */
    public static invalidateObjectPreviews(objectName: string): void {
        MaterialPreviews.previewRenderer.invalidateObjectCache(objectName);
    }

    /**
     * Clear all preview caches.
     * Call during scene transitions or when memory needs to be freed.
     */
    public static clearAllCaches(): void {
        MaterialPreviews.previewRenderer.clearCache();
    }

    /**
     * Get cache statistics for debugging.
     */
    public static getCacheStats(): { size: number; hits: number; misses: number; hitRate: number } {
        return MaterialPreviews.previewRenderer.getCacheStats();
    }
}
